import { EntityRepository } from '@record-it-npm/synchro-client';
import { useRef } from 'react';
import { useFunctionResult } from 'record-it-components/hooks/useFunctionResult/useFunctionResult';
import { EntityName } from 'record-it-time-common/EntityName';
import { ArrayUtils } from 'record-it-utils/utils/ArrayUtils/ArrayUtils';
import { StringUtils } from 'record-it-utils/utils/StringUtils/StringUtils';
import { EntityRepositoryByEntityName } from '../../../classes/entities/AppEntityManager';
import { useEntitiesChangedListener } from '../useEntitiesChangedListener/useEntitiesChangedListener';
import { useEntityManager } from '../useEntityManager';

export function useComputedEntityResult<
  TEntityNames extends Array<EntityName>,
  TResult
>({
  entityNames,
  callback,
  resultsAreEqual
}: UseComputedEntityResultOptions<TEntityNames, TResult>): TResult {
  const callbackOptions = useCallbackOptions({
    entityNames
  });

  const { result, update } = useFunctionResult({
    func: callback,
    funcArgs: [callbackOptions],
    resultsAreEqual
  });

  useEntitiesChangedListener(entityNames, update);

  return result;
}

function useCallbackOptions<TEntityNames extends Array<EntityName>>({
  entityNames
}: {
  entityNames: TEntityNames;
}): CallbackOptions<TEntityNames> {
  const { entityManager } = useEntityManager();
  const lastEntityNamesRef = useRef<Array<EntityName>>([]);
  const lastOptionsRef = useRef({});

  let options: Record<string, any>;

  if (!ArrayUtils.areShallowEqual(lastEntityNamesRef.current, entityNames)) {
    options = {
      repositories: []
    };

    const repositories = entityNames.map((entityName) =>
      entityManager.entityRepositoryContainer.getByEntityName(entityName)
    );

    for (const repository of repositories) {
      options[getRepositoryName(repository)] = repository;
      options.repositories.push(repository);
    }
  } else {
    options = lastOptionsRef.current;
  }

  lastEntityNamesRef.current = entityNames;
  lastOptionsRef.current = options;
  return options as CallbackOptions<TEntityNames>;
}

function getRepositoryName(
  repository: EntityRepository<any, any, any>
): string {
  const { entityName } = repository.getEntityInfo();
  return `${StringUtils.lowerCaseFirstLetter(entityName)}Repository`;
}

export type UseComputedEntityResultOptions<
  TEntityNames extends Array<EntityName>,
  TResult
> = {
  entityNames: TupleArray<TEntityNames>;
  callback: (options: CallbackOptions<TEntityNames>) => TResult;
  resultsAreEqual?: (a: TResult, b: TResult) => boolean;
};

export type Callback<TEntityNames extends Array<EntityName>, TResult> = (
  options: CallbackOptions<TEntityNames>
) => TResult;

export type CallbackOptions<TEntityNames extends Array<EntityName>> =
  UnionToIntersection<
    {
      [key in keyof TEntityNames]: TEntityNames[key] extends EntityName
        ? Record<
            RepositoryNameFromEntityName<TEntityNames[key]>,
            EntityRepositoryByEntityName[TEntityNames[key]]
          >
        : never;
    }[number]
  > & {
    repositories: Repositories<TEntityNames>;
  };

type RepositoryNameFromEntityName<TEntityName extends EntityName> =
  `${Uncapitalize<TEntityName>}Repository`;

export type Repositories<TEntityNames extends Array<EntityName>> = [
  ...{
    [key in keyof TEntityNames]: TEntityNames[key] extends EntityName
      ? EntityRepositoryByEntityName[TEntityNames[key]]
      : never;
  }
];

// TODO: move to Utils
/**
 * Converts an union type to an intersection
 * This is useful if you have a template parameter of an union type (e.g. a union of supported PropertyTypes etc.) and want it to generate a single object instead of an union of objects
 *
 * Example:
 *   UnionToIntersection<{ a: number, c: number } | { a: number, b:number }> // returns { a: number, b: number } & { a: number, c: number }
 *
 * https://stackoverflow.com/questions/50374908/transform-union-type-to-intersection-type/50375286#50375286
 */
export type UnionToIntersection<U> = (
  U extends any ? (k: U) => void : never
) extends (k: infer I) => void
  ? I
  : never;

/**
 * with this you can automatically infer an array as a tuple
 *
 * e.g.
 *    function doSomething<TArrayItems extends Array<any>>(array: TupleArray<TArrayItems>): TArrayItems { return array }
 * typescript automatically detect that the return type is the same as the input type
 *   doSomething(['string', 5])[0].length -> this wouldn't work without the utility type because typescript would think that the return type is Array<string|number> instead of [string, number]
 *
 */
export type TupleArray<TTuple extends Array<unknown>> = [
  ...{ [K in keyof TTuple]: TTuple[K] }
];
